import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  SUBTYPES_BY_TYPE,
  accountsApi,
  type Account,
  type AccountSubtype,
  type AccountType,
} from '@/api/accounts';
import { connectorsApi, type ConnectorMapping } from '@/api/connectors';
import { ErrorCodes, isApiError } from '@/api/errors';
import { ConfirmPanel } from '@/components/ConfirmDialog';
import { useReturnFocus } from '@/components/useConfirm';
import { SubmitButton } from '@/components/SubmitButton';
import { ChooseHowWeHelpButton } from '@/features/automation/ChooseHowWeHelpButton';
import { ConsentStep } from '@/features/automation/ConsentStep';
import { useAutomationGate } from '@/features/automation/useAutomationGate';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { SUBSCRIBE_CURRENCIES } from '@/features/subscribe/currencies';
import { applyBackendFields, validationAlert } from '@/features/shared/useFieldErrors';
import { isOwnerAccount } from '@/features/accounts/accountAccess';
import { mapProviderType } from './providerTypes';
import type { BankAccountMeta, MappingPayload } from './useBankConnect';
import { useErrorFocus } from '@/shared/useErrorFocus';
import { useUnsavedGuard } from '@/shared/useUnsavedGuard';

const TITLE_ID = 'map-accounts-title';
const DISCARD_TITLE_ID = 'map-accounts-discard-title';
const DISCARD_BODY_ID = 'map-accounts-discard-body';
const CONSENT_TITLE_ID = 'map-accounts-consent-title';
const CONSENT_OPENER = 'map-consent';
const CONNECT_OPENER = 'map-connect';
const TYPES: AccountType[] = ['CASH', 'REVOLVING', 'LOAN'];
const SKIP = '__skip__';
const CREATE = '__create__';

type RowChoice = 'skip' | 'existing' | 'create';

interface RowState {
  choice: RowChoice;
  /** Not mappable yet (file import only): the row can only be skipped. */
  unsupported: boolean;
  accountId: number | null;
  name: string;
  type: AccountType;
  subtype: AccountSubtype;
  currency: string;
}

interface MapAccountsDialogProps {
  mapping: MappingPayload;
  operatingCurrency: string | null;
  onClose: () => void;
  onConnected: () => void | Promise<void>;
  /** `complete` answered 404 `CONNECTOR_SESSION_NOT_FOUND`: the session is spent — never resent. */
  onSessionExpired: () => void;
}

function currencyOptions(operating: string | null): string[] {
  const rest = SUBSCRIBE_CURRENCIES.filter((code) => code !== operating);
  return operating === null ? rest : [operating, ...rest];
}

function initialRow(
  provider: MappingPayload['provider'],
  account: BankAccountMeta,
  operating: string | null,
): RowState {
  const mapped = mapProviderType(provider, account.type, account.subtype);
  return {
    choice: mapped === null ? 'skip' : 'create',
    unsupported: mapped === null,
    accountId: null,
    name: account.name,
    type: mapped?.type ?? 'CASH',
    subtype: mapped?.subtype ?? 'OTHER',
    currency: account.currency ?? operating ?? '',
  };
}

/** Row indexes grouped by institution, in first-seen order. */
function groupByInstitution(
  accounts: BankAccountMeta[],
): { institution: string; rows: number[] }[] {
  const groups: { institution: string; rows: number[] }[] = [];
  accounts.forEach((account, index) => {
    const group = groups.find((row) => row.institution === account.institution);
    if (group) group.rows.push(index);
    else groups.push({ institution: account.institution, rows: [index] });
  });
  return groups;
}

/**
 * Accounts another live connection already feeds. A removed connection holds none (TMMAPI-0096: a
 * new link to a bank the owner removed carries no mappings), so its accounts stay offered.
 */
function mappedAccountIds(
  items: { status: string; accounts: { accountId: number | null }[] }[],
): Set<number> {
  const ids = new Set<number>();
  for (const item of items) {
    if (item.status === 'REMOVED') continue;
    for (const account of item.accounts) {
      if (account.accountId !== null) ids.add(account.accountId);
    }
  }
  return ids;
}

const MAPPING_FIELD = /^mappings\[(\d+)\]\.(.+)$/;

/** Server `mappings[i]` is the submitted (non-skipped) list; remap onto the UI row. */
function toUiMappingField(field: string, uiIndexes: number[]): string {
  const match = MAPPING_FIELD.exec(field);
  if (match === null) return field;
  const uiIndex = uiIndexes[Number(match[1])];
  if (uiIndex === undefined) return field;
  return `mappings[${uiIndex}].${match[2]}`;
}

export function MapAccountsDialog({
  mapping,
  operatingCurrency,
  onClose,
  onConnected,
  onSessionExpired,
}: MapAccountsDialogProps) {
  const { t } = useTranslation();
  const gate = useAutomationGate();
  const [rows, setRows] = useState<RowState[]>(() =>
    mapping.accounts.map((account) => initialRow(mapping.provider, account, operatingCurrency)),
  );
  const groups = groupByInstitution(mapping.accounts);
  const stripe = mapping.provider === 'STRIPE_FC';
  const [owned, setOwned] = useState<Account[]>([]);
  const [takenIds, setTakenIds] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [listUnavailable, setListUnavailable] = useState(false);
  const [completeUnavailable, setCompleteUnavailable] = useState(false);
  const [subscribeRequired, setSubscribeRequired] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  // "Choose how we help first" shows the consent step in place of the mapping, in this same
  // dialog (DLG-005, TMMAPP-0097 §C 23); Esc and Cancel return to the mapping.
  const [consenting, setConsenting] = useState(false);
  // The open mapping is the unsaved work — the bank link is not made until it is confirmed and
  // its single-use session cannot be resumed — so a route change or a reload asks the same
  // question as Cancel does, in the same place (TMMAPP-0073).
  const nav = useUnsavedGuard(true, { busy: submitting, includeSearch: true });
  const asking = confirmClose || nav.blocked;
  const { rootRef, focusErrors } = useErrorFocus<HTMLDivElement>();
  const started = useRef(false);

  async function loadLists() {
    try {
      const [accounts, items] = await Promise.all([accountsApi.list(), connectorsApi.list()]);
      setOwned(
        accounts.filter((account) => isOwnerAccount(account) && account.status !== 'CLOSED'),
      );
      setTakenIds(mappedAccountIds(items));
      setListUnavailable(false);
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.CONNECTOR_UNAVAILABLE) {
        setListUnavailable(true);
      }
    }
  }

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void loadLists();
  }, []);

  const unavailable = listUnavailable || completeUnavailable;

  function setRow(index: number, patch: Partial<RowState>) {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function handleChoice(index: number, value: string) {
    if (value === SKIP) {
      setRow(index, { choice: 'skip', accountId: null });
      return;
    }
    if (value === CREATE) {
      setRow(index, { choice: 'create', accountId: null });
      return;
    }
    const accountId = Number(value);
    setRow(index, { choice: 'existing', accountId: Number.isFinite(accountId) ? accountId : null });
  }

  function handleType(index: number, type: AccountType | null) {
    if (type === null) return;
    setRow(index, { type, subtype: SUBTYPES_BY_TYPE[type][0]! });
  }

  // "Keep matching" returns focus to the mapping's Cancel, which opened the confirmation.
  useReturnFocus(asking, 'map-cancel');
  // Back from the consent step: to "Choose how we help first", or, once consent is answered and
  // that button is gone, to Connect.
  useReturnFocus(consenting, gate.needsConsent ? CONSENT_OPENER : CONNECT_OPENER);
  // The consent step's heading takes focus as it replaces the mapping.
  useEffect(() => {
    if (consenting) document.getElementById(CONSENT_TITLE_ID)?.focus();
  }, [consenting]);

  function handleRequestClose() {
    if (submitting) return;
    setConfirmClose(true);
  }

  function handleDiscard() {
    setConfirmClose(false);
    if (nav.blocked) nav.proceed();
    else onClose();
  }

  function handleKeepMatching() {
    setConfirmClose(false);
    nav.stay();
  }

  function collectMappings(): { mappings: ConnectorMapping[]; uiIndexes: number[] } {
    const mappings: ConnectorMapping[] = [];
    const uiIndexes: number[] = [];
    mapping.accounts.forEach((account, index) => {
      const row = rows[index];
      if (row === undefined || row.choice === 'skip') return;
      if (row.choice === 'existing' && row.accountId !== null) {
        mappings.push({ providerAccountId: account.id, accountId: row.accountId });
        uiIndexes.push(index);
        return;
      }
      if (row.choice === 'create') {
        mappings.push({
          providerAccountId: account.id,
          create: {
            name: row.name.trim(),
            type: row.type,
            subtype: row.subtype,
            currency: row.currency,
          },
        });
        uiIndexes.push(index);
      }
    });
    return { mappings, uiIndexes };
  }

  async function handleConnect() {
    if (submitting || gate.needsConsent) return;
    setSubmitting(true);
    setAlert(null);
    setFieldErrors({});
    const collected = collectMappings();
    try {
      await connectorsApi.complete(
        mapping.provider === 'PLAID'
          ? { provider: 'PLAID', publicToken: mapping.publicToken, mappings: collected.mappings }
          : { provider: 'STRIPE_FC', sessionRef: mapping.sessionRef, mappings: collected.mappings },
      );
      await onConnected();
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.CONNECTOR_SESSION_NOT_FOUND) {
        onSessionExpired();
        return;
      }
      if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setSubscribeRequired(true);
      } else if (isApiError(error) && error.code === ErrorCodes.CONNECTOR_UNAVAILABLE) {
        setCompleteUnavailable(true);
      } else if (isApiError(error) && error.code === ErrorCodes.CONNECTOR_MAPPING_INVALID) {
        const known = collected.mappings.flatMap((_, i) => [
          `mappings[${i}].providerAccountId`,
          `mappings[${i}].accountId`,
          `mappings[${i}].create`,
          `mappings[${i}].create.name`,
          `mappings[${i}].create.type`,
          `mappings[${i}].create.subtype`,
          `mappings[${i}].create.currency`,
        ]);
        const result = applyBackendFields(
          error,
          known,
          (field, message) => {
            const uiField = toUiMappingField(field, collected.uiIndexes);
            setFieldErrors((prev) => ({ ...prev, [uiField]: message }));
          },
          t,
          focusErrors,
        );
        setAlert(validationAlert(result, error, t, t('errors.checkFields')));
      } else {
        setAlert(t('accounts.errorGeneric'));
        focusErrors();
      }
      setSubmitting(false);
    }
  }

  async function handleRetry() {
    if (listUnavailable) {
      await loadLists();
      return;
    }
    setCompleteUnavailable(false);
    await handleConnect();
  }

  return (
    // Discarding confirms in place of the mapping — dialogs never stack (DLG-005).
    <Dialog
      open
      onClose={
        asking ? handleKeepMatching : consenting ? () => setConsenting(false) : handleRequestClose
      }
      aria-labelledby={asking ? DISCARD_TITLE_ID : consenting ? CONSENT_TITLE_ID : TITLE_ID}
      aria-describedby={asking ? DISCARD_BODY_ID : undefined}
      fullWidth
      maxWidth="sm"
    >
      {asking ? (
        <ConfirmPanel
          titleId={DISCARD_TITLE_ID}
          consequenceId={DISCARD_BODY_ID}
          title={
            mapping.institution.length > 0
              ? t('connections.discardNamedTitle', { institution: mapping.institution })
              : t('connections.discardTitle')
          }
          consequence={t('connections.discardConsequence')}
          confirmLabel={t('connections.discardConfirm')}
          cancelLabel={t('connections.keepMatching')}
          destructive
          focusHeading
          onConfirm={handleDiscard}
          onCancel={handleKeepMatching}
        />
      ) : consenting && gate.catalog !== null ? (
        <>
          <DialogTitle id={CONSENT_TITLE_ID} tabIndex={-1}>
            {t('consent.title')}
          </DialogTitle>
          <ConsentStep
            variant="full"
            layout="dialog"
            catalog={gate.catalog}
            cancelLabel={t('common.cancel')}
            onCancel={() => setConsenting(false)}
            onCatalogChange={() => void gate.reload()}
            onCompleted={() => {
              // The mapping comes back once the gate knows consent is answered.
              void gate.reload().finally(() => setConsenting(false));
            }}
          />
        </>
      ) : (
        <div ref={rootRef} style={{ display: 'contents' }}>
          <DialogTitle id={TITLE_ID}>
            {mapping.institution.length > 0
              ? t('connections.mapTitle', { institution: mapping.institution })
              : t('connections.mapTitleMany')}
          </DialogTitle>
          <DialogContent>
            {subscribeRequired ? (
              <SubscribeInPlace onClose={onClose} />
            ) : unavailable ? (
              <Stack spacing={2} sx={{ mt: 0.5 }}>
                <Alert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" onClick={() => void handleRetry()}>
                      {t('common.retry')}
                    </Button>
                  }
                >
                  {t('connections.unavailable')}
                </Alert>
              </Stack>
            ) : (
              <Stack spacing={2} sx={{ mt: 0.5 }}>
                {alert !== null && <Alert severity="error">{alert}</Alert>}
                {stripe && (
                  <Alert severity="info">
                    <Stack spacing={0.5}>
                      <span>{t('connections.historyNote')}</span>
                      <span>{t('connections.usOnly')}</span>
                    </Stack>
                  </Alert>
                )}
                {groups.map((group) => (
                  <Stack key={group.institution} spacing={2}>
                    {groups.length > 1 && (
                      <Typography variant="subtitle1" component="h3">
                        {group.institution}
                      </Typography>
                    )}
                    {group.rows.map((index) => {
                      const account = mapping.accounts[index]!;
                      const row = rows[index]!;
                      const selected =
                        row.choice === 'skip'
                          ? SKIP
                          : row.choice === 'create'
                            ? CREATE
                            : String(row.accountId ?? '');
                      const selectedAccount =
                        row.accountId !== null
                          ? owned.find((item) => item.id === row.accountId)
                          : undefined;
                      const mismatch =
                        selectedAccount !== undefined &&
                        selectedAccount.currency !== null &&
                        account.currency !== null &&
                        selectedAccount.currency !== account.currency;
                      const nameError = fieldErrors[`mappings[${index}].create.name`];
                      return (
                        <Stack key={account.id} spacing={1} component="section">
                          <Typography
                            variant="subtitle2"
                            component={groups.length > 1 ? 'h4' : 'h3'}
                          >
                            {account.name}
                            {account.mask !== null && account.mask.length > 0
                              ? ` · ${t('connections.mask', { mask: account.mask })}`
                              : ''}
                            {account.type.length > 0
                              ? ` · ${account.type}${account.subtype.length > 0 ? `/${account.subtype}` : ''}`
                              : ''}
                            {account.currency !== null ? ` · ${account.currency}` : ''}
                          </Typography>
                          {row.unsupported ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {t('connections.notSupported')}
                            </Typography>
                          ) : (
                            <FormControl fullWidth>
                              <InputLabel id={`map-to-${account.id}`}>
                                {t('connections.mapTo')}
                              </InputLabel>
                              <Select
                                labelId={`map-to-${account.id}`}
                                label={t('connections.mapTo')}
                                value={selected}
                                onChange={(event) => handleChoice(index, event.target.value)}
                              >
                                <MenuItem value={SKIP}>{t('connections.skip')}</MenuItem>
                                {owned
                                  .filter((item) => !takenIds.has(item.id))
                                  .map((item) => (
                                    <MenuItem key={item.id} value={String(item.id)}>
                                      {item.name}
                                      {item.currency !== null ? ` (${item.currency})` : ''}
                                    </MenuItem>
                                  ))}
                                <MenuItem value={CREATE}>{t('connections.createNew')}</MenuItem>
                              </Select>
                              {mismatch && (
                                <FormHelperText>{t('connections.currencyMismatch')}</FormHelperText>
                              )}
                            </FormControl>
                          )}
                          {row.choice === 'create' && (
                            <Stack spacing={1.5}>
                              <TextField
                                label={t('accounts.nameLabel')}
                                value={row.name}
                                onChange={(event) => setRow(index, { name: event.target.value })}
                                required
                                fullWidth
                                error={nameError !== undefined}
                                helperText={nameError}
                              />
                              <FormControl>
                                <ToggleButtonGroup
                                  exclusive
                                  size="small"
                                  value={row.type}
                                  onChange={(_event, value: AccountType | null) =>
                                    handleType(index, value)
                                  }
                                  aria-label={t('accounts.typeLabel')}
                                >
                                  {TYPES.map((type) => (
                                    <ToggleButton key={type} value={type}>
                                      {t(`accounts.type.${type}`)}
                                    </ToggleButton>
                                  ))}
                                </ToggleButtonGroup>
                              </FormControl>
                              <FormControl fullWidth>
                                <InputLabel id={`map-subtype-${account.id}`}>
                                  {t('accounts.subtypeLabel')}
                                </InputLabel>
                                <Select
                                  labelId={`map-subtype-${account.id}`}
                                  label={t('accounts.subtypeLabel')}
                                  value={row.subtype}
                                  onChange={(event) =>
                                    setRow(index, { subtype: event.target.value as AccountSubtype })
                                  }
                                >
                                  {SUBTYPES_BY_TYPE[row.type].map((subtype) => (
                                    <MenuItem key={subtype} value={subtype}>
                                      {t(`accounts.subtype.${subtype}`)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControl fullWidth>
                                <InputLabel id={`map-currency-${account.id}`}>
                                  {t('accounts.currencyLabel')}
                                </InputLabel>
                                <Select
                                  labelId={`map-currency-${account.id}`}
                                  label={t('accounts.currencyLabel')}
                                  value={row.currency}
                                  onChange={(event) =>
                                    setRow(index, { currency: event.target.value })
                                  }
                                >
                                  {currencyOptions(operatingCurrency).map((code) => (
                                    <MenuItem key={code} value={code}>
                                      {code}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          )}
                        </Stack>
                      );
                    })}
                  </Stack>
                ))}
                {stripe && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('connections.skippedNotBilled')}
                  </Typography>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleRequestClose}
              disabled={submitting}
              data-confirm-opener="map-cancel"
            >
              {t('common.cancel')}
            </Button>
            {!subscribeRequired && !unavailable && gate.loading && (
              <Typography variant="body2">{t('common.loading')}</Typography>
            )}
            {!subscribeRequired && !unavailable && !gate.loading && gate.needsConsent && (
              <ChooseHowWeHelpButton
                catalog={gate.catalog}
                onRequest={() => setConsenting(true)}
                openerKey={CONSENT_OPENER}
              />
            )}
            {!subscribeRequired && !unavailable && !gate.needsConsent && (
              <SubmitButton
                loading={submitting}
                data-confirm-opener={CONNECT_OPENER}
                onClick={() => void handleConnect()}
              >
                {t('connections.connectAction')}
              </SubmitButton>
            )}
          </DialogActions>
        </div>
      )}
    </Dialog>
  );
}
